import Cookies from 'js-cookie';
import { CartNs } from 'ReactApp/Types/CartNs';
import { ProductTypeEnum } from 'ReactApp/Types/enum';
import { 
    makeObservable, observable, 
    computed, action, autorun, trace
} from "mobx";
import { csrfTokenInstance } from 'ReactApp/Services/CsrfToken/CsrfToken';
import { DomainNs } from 'ReactApp/Types/DomainNs';

class Cart
    implements CartNs.CartInterface
{
    private static _instance: Cart;

    productSet: CartNs.CartProductSetInterface | null = null;
    counterTracker: CartNs.CounterTrackerInterface | null = null;

    private constructor() {
        this.initComponent();

        makeObservable(this, {
            productSet: observable,
            // calculated/action/flow/etc
            summaryCount: computed,
            initComponent: action,
            fillElements: action,
            addOrChange: action,
        });
        makeObservable((this.productSet as object), {
            products: observable,
            summaryCount: computed,
            addProduct: action,
            changeProduct: action,
            delete: action,
            find: action,
            findIndex: action,
        });
    }

    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    async updateAll(): Promise<boolean> {
        let that = this;

        let data: any = {
            cart: that.productSet
        };
        data[csrfTokenInstance.name] = csrfTokenInstance.content;

        const response = await fetch('/cart/update-all', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data),
        }).catch(err => {
            console.error(`The Cart updateAll point given error: ${err.message}`);
        });

        if (response instanceof Response && response.ok === true) {
            let rawElements = await response.json();
            that.fillElements(rawElements);
        }

        return new Promise(resolve => resolve(true));
    }

    initComponent(): void {
        this.productSet = new CartProductSet();
        this.counterTracker = new CounterTracker(
            this
        );
        let rawCart = [];
        try {
            rawCart = JSON.parse(Cookies.get('cart') as string);
        } catch (e) {
            // TODO[needImpl]: after logging injection: add info
            // about invalid parse a cart-data
            rawCart = [];
        }
        this.fillElements(rawCart);
    }

    /**
     * Отменяем промо-код
     * @returns
     */
    cancelPromoCode = async (): Promise<boolean> => {
        let promise: Promise<boolean> = new Promise(
            resolve => resolve(false)
        );
        const response = await fetch(
            "/cart/cancel-promo-code"
        ).catch(err => {
            promise = new Promise(resolve => resolve(false));
        });

        if (response instanceof Response && response.ok === true) {
            promise = new Promise(resolve => resolve(true));
        }

        return promise;
    }

    /**
     * 
     * @param string code значение промо-кода
     * @returns
     */
    applyPromoCode = async (code: string): Promise<string|boolean> => {
        let promise: Promise<string|boolean> = new Promise(
            resolve => resolve(false)
        );
        const response = await fetch(
            `/cart/apply-promo-code?code=${code}`
        ).catch(err => {
            promise = new Promise(resolve => resolve(false));
        });
        
        if (
            response instanceof Response 
                && 
            response.ok === true
        ) {
            promise = new Promise(resolve => resolve(true));
        }
        
        if (
            response instanceof Response 
                && 
            [410, 400].includes(response.status)
        ) {
            await response.json().then(data => {
                promise = new Promise(resolve => resolve(data.message));
            });
        }

        return promise;
    }

    fillElements(rawElements: {
        productTypeId: ProductTypeEnum,
        id: number,
        count: number,
        isDeleted: boolean,
        virtualModifierId?: number
    }[]) {

        let that = this;

        if (that.productSet === null) {
            return;
        }
        // clear old values
        that.productSet.products = [];
        
        for (let key in rawElements) {
            switch (rawElements[key].productTypeId) {
                case ProductTypeEnum.card:
                    that.productSet.products.push(
                        new CardCartProduct(rawElements[key])
                    );
                break;
                case ProductTypeEnum.realModifier:
                    let productData: any = {
                        id: rawElements[key].id,
                        count: rawElements[key].count,
                    }
                    if (
                        rawElements[key].virtualModifierId !== undefined
                    ) {
                        productData.virtualModifierId = 
                            rawElements[key].virtualModifierId;
                    }
                    that.productSet.products.push(
                        new RealModifierCartProduct(productData)
                    );
                break;
            }
        }
    }

    addOrChange(
        productTypeId: ProductTypeEnum,
        id: number,
        count: number,
        virtualModifierId?: number
    ): boolean
    {
        let that = this;

        if (that.productSet === null) {
            return false;
        }
        const product = that.productSet.find(
            productTypeId,
            id,
            (virtualModifierId !== undefined)
                ? virtualModifierId
                : undefined
        );

        if (product !== null) {
            // ... NOT ALLOW CHANGE?
            return true;
        }

        that.productSet.addProduct(
            productTypeId,
            id,
            count,
            false,
            (virtualModifierId !== undefined)
                ? virtualModifierId
                : undefined
        );
        that.updateAll();
        return true;
    }

    get summaryCount(): number {
        return this.productSet?.summaryCount || 0;
    }

}

class CounterTracker
    implements CartNs.CounterTrackerInterface
{
    cart!: Cart;

    trackerSelector = [
        {
            counterSelector: '.site-header__indicators-link._cart .site-header__indicators-count span',
            wrapSelector: '.site-header__indicators-link._cart .site-header__indicators-count'
        }
    ];

    constructor(cart: Cart) {
        this.cart = cart;
    }

    update(): void {
        let that = this;
        for (let key in that.trackerSelector) {
            let counterSelector = that.trackerSelector[key].counterSelector;
            let wrapSelector = that.trackerSelector[key].wrapSelector;

            const counterElement = document.querySelector(counterSelector);
            const wrapElement = document.querySelector(wrapSelector);

            if (
                !(counterElement instanceof Element) 
                    || 
                !(wrapElement instanceof Element)
            ) {
                return;
            }

            if (that.cart.summaryCount > 0) {
                wrapElement.classList.remove('_hide');
                counterElement.innerHTML = 
                    `${that.cart.summaryCount}`;
            } else {
                wrapElement.classList.add('_hide');
            }

        }

    }

}

class CartProductSet
    implements CartNs.CartProductSetInterface
{
    products: (
        CartNs.CardCartProductInterface | 
        CartNs.RealModifierCartProductInterface
    )[] = [];
    
    public get summaryCount(): number {
        let count = 0;
        this.products.forEach(product => {
            if (product.isDeleted) {
                return;
            }
            count += product.count;
        });
        return count;
    }

    addProduct(
        productTypeId: ProductTypeEnum,
        id: number,
        count: number,
        isDeleted: boolean = false,
        virtualModifierId?: number
    ): void
    {
        switch (productTypeId) {
            case ProductTypeEnum.card:
                this.products.push(new CardCartProduct({
                    id: id,
                    count: count,
                    isDeleted: isDeleted
                }));
            break;
            case ProductTypeEnum.realModifier:
                let productData: any = {
                    id: id,
                    count: count,
                    isDeleted: isDeleted
                };
                if (virtualModifierId !== undefined) {
                    productData.virtualModifierId = virtualModifierId;
                }
                this.products.push(new RealModifierCartProduct(productData));
            break;
        }
    }

    changeProduct(
        product: CartNs.BaseCartProductInterface,
        count: number,
        isDeleted: boolean = false,
        virtualModifierId?: number,
    ): void
    {
        product.count = count;
        product.isDeleted = isDeleted;
        if (virtualModifierId !== undefined) {
            (product as CartNs.RealModifierCartProductInterface)
                .virtualModifier = new VirtualModifierCartProduct({
                    id: virtualModifierId
                });
        }
    }

    delete(
        productTypeId: ProductTypeEnum,
        id: number,
    ): void
    {
        const findIndex = this.findIndex(productTypeId, id);
        if (typeof findIndex === 'number') {
            this.products.splice(findIndex, 1);
        }
    }

    markAsDeleted(
        productTypeId: ProductTypeEnum,
        id: number,
    ): void
    {
        const findIndex = this.findIndex(productTypeId, id);
        if (typeof findIndex === 'number') {
            this.products[findIndex].isDeleted = true;
        }
    }

    markAsUnDeleted(
        productTypeId: ProductTypeEnum,
        id: number,
    ): void
    {
        const findIndex = this.findIndex(productTypeId, id);
        if (typeof findIndex === 'number') {
            this.products[findIndex].isDeleted = false;
        }
    }

    find(
        productTypeId: ProductTypeEnum,
        id: number,
        virtualModifierId?: number
    ): CardCartProduct | RealModifierCartProduct | null
    {
        return this.products.find(p => {
            let check = (
                p.productTypeId === productTypeId
                    &&
                p.id === id
            );

            switch (p.productTypeId) {
                case ProductTypeEnum.realModifier:

                    // if it's RM's without virtualModifier
                    if (
                        check === true 
                            && 
                        p.productTypeId === ProductTypeEnum.realModifier
                            &&
                        virtualModifierId === undefined
                            &&
                        p.virtualModifier !== null
                    ) {
                        return false;
                    }
                    // \if it's RM's without virtualModifier

                    if (virtualModifierId !== undefined) {
                        if ((p as RealModifierCartProduct).virtualModifier === null) {
                            return false;
                        }
                        check = (
                            check 
                                && 
                            (p as RealModifierCartProduct).virtualModifier?.id
                                ===
                            virtualModifierId
                        );
                    }

                break;
            }

            return check;
        }) || null;
    }

    findIndex(
        productTypeId: ProductTypeEnum,
        id: number,
        virtualModifierId?: number
    ): number | null
    {
        return this.products.findIndex(p => {
            let check = (
                p.productTypeId === productTypeId
                    &&
                p.id === id
            );

            switch (p.productTypeId) {
                case ProductTypeEnum.realModifier:

                    // if it's RM's without virtualModifier
                    if (
                        check === true 
                            && 
                        p.productTypeId === ProductTypeEnum.realModifier
                            &&
                        virtualModifierId === undefined
                            &&
                        p.virtualModifier !== null
                    ) {
                        return false;
                    }
                    // \if it's RM's without virtualModifier

                    if (virtualModifierId !== undefined) {
                        if ((p as RealModifierCartProduct).virtualModifier === null) {
                            return false;
                        }
                        check = (
                            check 
                                && 
                            (p as RealModifierCartProduct).virtualModifier?.id
                                ===
                            virtualModifierId
                        );
                    }

                break;
            }

            return check;
        }) || null;
    }
}

class BaseCartProduct
    implements CartNs.BaseCartProductInterface
{
    id!: number;
    productTypeId!: ProductTypeEnum;
    count!: number;
    isDeleted: boolean = false;
}

export class CardCartProduct
    implements CartNs.CardCartProductInterface
{
    productTypeId: ProductTypeEnum.card = 
        ProductTypeEnum.card;
    id!: number;
    count!: number;
    isDeleted: boolean = false;
    
    constructor(cartElement: {
        id: number,
        count: number,
        isDeleted: boolean,
    }) {
        this.id = cartElement.id;
        this.count = cartElement.count;
        this.isDeleted = cartElement.isDeleted;
    }
}

export class RealModifierCartProduct
    implements CartNs.RealModifierCartProductInterface
{
    productTypeId: ProductTypeEnum.realModifier = 
        ProductTypeEnum.realModifier;
    id!: number;
    count!: number;
    isDeleted: boolean = false;
    virtualModifier: CartNs.VirtualModifierCartProductInterface | null = null;

    constructor(cartElement: {
        id: number,
        count: number,
        isDeleted: boolean,
        virtualModifierId?: number,
        showableCard?: DomainNs.ProductCardV6Interface | DomainNs.ProductCardV7Interface;
    }) {
        this.id = cartElement.id;
        this.count = cartElement.count;
        this.isDeleted = cartElement.isDeleted;
        if (cartElement.virtualModifierId !== undefined) {
            this.virtualModifier = new VirtualModifierCartProduct({
                id: cartElement.virtualModifierId,
            });
        }
    }
}

export class VirtualModifierCartProduct
    implements CartNs.VirtualModifierCartProductInterface
{
    productTypeId: ProductTypeEnum.virtualModifier
        = ProductTypeEnum.virtualModifier;
    id!: number;
    count!: number;

    constructor(cartElement: {
        id: number,
    }) {
        this.id = cartElement.id;
    }
}

const cartInstance = Cart.Instance;

export { cartInstance };